import { PriceItem } from '../../../../types/price-processing.types';

/**
 * Orders stations by province with hierarchical grouping
 * Groups OKKO stations first, then their bound competitors, then orphaned and unbound competitors
 * 
 * @param data - Array of price items to order
 * @returns Ordered array of price items grouped by province and binding status
 */
export const orderStationsByProvince = (data: PriceItem[]): PriceItem[] => {
  const orderedStations: PriceItem[] = [];
  const processedStations = new Set<string>();
  
  const provinceGroups = data.reduce((groups, station) => {
    const province = station.province || 'Unknown';
    if (!groups[province]) groups[province] = [];
    groups[province].push(station);
    return groups;
  }, {} as {[province: string]: PriceItem[]});
  
  Object.keys(provinceGroups).sort().forEach(province => {
    const provinceStations = provinceGroups[province];
    
    const allOkkoStations = provinceStations.filter(s => s.is_reference);
    allOkkoStations.forEach(okkoStation => {
      if (!okkoStation.unicode || processedStations.has(okkoStation.unicode)) return;
      
      orderedStations.push(okkoStation);
      processedStations.add(okkoStation.unicode);
      
      const boundCompetitors = provinceStations.filter(s => {
        if (s.is_reference || !s.binded || !s.binded_to || !okkoStation.unicode) return false;
        const bindings = Array.isArray(s.binded_to) ? s.binded_to : [s.binded_to];
        return bindings.includes(okkoStation.unicode);
      });
      
      boundCompetitors.forEach(competitor => {
        if (!competitor.unicode || !okkoStation.unicode) return;
        
        const competitorCopy = {
          ...competitor,
          binded_to: okkoStation.unicode,
          _originalUnicode: competitor.unicode,
          _bindingContext: okkoStation.unicode
        };
        
        const uniqueKey = `${competitor.unicode}-bound-to-${okkoStation.unicode}`;
        if (!processedStations.has(uniqueKey)) {
          orderedStations.push(competitorCopy);
          processedStations.add(uniqueKey);
        }
      });
    });
    
    const orphanedBoundCompetitors = provinceStations.filter(s => {
      if (s.is_reference || !s.binded || !s.binded_to) return false;
      const bindings = Array.isArray(s.binded_to) ? s.binded_to : [s.binded_to];
      const hasOrphanedBindings = bindings.some(binding => 
        !provinceStations.some(okko => okko.unicode === binding)
      );
      return hasOrphanedBindings && s.unicode && !processedStations.has(s.unicode);
    });
    
    orphanedBoundCompetitors.forEach(station => {
      if (station.unicode && !processedStations.has(station.unicode)) {
        orderedStations.push(station);
        processedStations.add(station.unicode);
      }
    });
    
    const unboundCompetitors = provinceStations.filter(s => 
      !s.is_reference && !s.binded && s.unicode && !processedStations.has(s.unicode)
    );
    unboundCompetitors.forEach(station => {
      if (station.unicode) {
        orderedStations.push(station);
        processedStations.add(station.unicode);
      }
    });
  });
  
  return orderedStations;
};

/**
 * Determines CSS class for table row based on station type and binding status
 * 
 * @param item - Price item to determine class for
 * @param isStationTable - Whether this is for station-level table (vs summary table)
 * @returns CSS class string for the row
 */
export const getRowClass = (item: PriceItem, isStationTable: boolean = false): string => {
  if (item.is_reference) {
    return 'price-processing-table-row okko-row';
  }
  if (isStationTable) {
    if (!item.binded) {
      return 'price-processing-table-row unbound-competitor';
    }
    return 'price-processing-table-row bound-competitor';
  }
  return `price-processing-table-row ${!item.isOkko && item.binded === false ? 'binded-competitor' : ''}`;
};

/**
 * Sorts summary data by province and station type
 * Orders by province alphabetically, then OKKO stations first, then competitors
 * 
 * @param data - Array of price items to sort
 * @returns Sorted array of price items
 */
export const sortSummaryData = (data: PriceItem[]): PriceItem[] => {
  return [...data].sort((a, b) => {
    const provinceCompare = (a.province || '').localeCompare(b.province || '');
    if (provinceCompare !== 0) return provinceCompare;
    if (a.isOkko && !b.isOkko) return -1;
    if (!a.isOkko && b.isOkko) return 1;
    return a.brand.localeCompare(b.brand);
  });
};

export const formatToLocalDateTime = (date: string | Date): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString('uk-UA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};
